import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { toast } from 'react-hot-toast';
import { Trash2, RotateCcw, Zap, Plus, Check, X } from 'react-feather';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import LoadingScreen from '../components/LoadingScreen';
import { InlineSelect, InlineCheckbox, BulkActionBar, StatusDot, SortIcon } from '../components/InlineTable';
import {
  ConfigPageShell, ScopeSelect, ConfigToolbar, ToolbarButton, ConfigEmptyState, ConfirmPopover,
} from '../components/ConfigPages';
import {
  getJournalEntryTypes, updateJournalEntryType, deleteJournalEntryType, restoreJournalEntryType,
  bulkUpdateJournalEntryTypes, createJournalEntryType, getChartOfAccounts,
} from '../utils/api';
import { getLocationsByOrgId } from '../utils/locationApi';
import AiJournalCoaMappingModal from '../components/AiJournalCoaMappingModal';

const CATEGORY_OPTIONS = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'tax', label: 'Tax' },
  { value: 'payments', label: 'Payments' },
  // Guest/Deposit ledger movement types (map to A/R + advance-deposit accounts).
  { value: 'ledger', label: 'Ledger' },
];

const EMPTY_DRAFT = { name: '', category: null, chartOfAccountId: null };

/** "Rooms · Revenue" — the department and account type a mapped journal type
 *  lands under in the export (the account number itself is already in the select). */
const landsIn = (acct) => {
  if (!acct) return null;
  const parts = [acct.department, acct.accountType].filter((p) => p != null && String(p).trim() !== '');
  return parts.length ? parts.join(' · ') : null;
};

/** Status of one row: state for the StatusDot + a title that says why. */
const rowStatus = (type, acct) => {
  if (type.isDeleted) return { state: 'none', title: 'Deleted — excluded from the journal export' };
  if (!type.chartOfAccountId) return { state: 'partial', title: 'Unmapped — lands in the export\'s residual line' };
  if (!acct) return { state: 'partial', title: 'Account deleted or not in this location — export treats as unmapped' };
  return { state: 'complete', title: `Mapped to ${acct.accountNumber || acct.accountName}${landsIn(acct) ? ` · ${landsIn(acct)}` : ''}` };
};

/** Journal entry type row — checkbox, read-only name, category + COA inline selects, status, actions. */
const JournalTypeRow = ({
  type, acct, coaOptions, saving, selected, onSelect,
  onCategoryChange, onCoaChange, onDelete, onRestore,
}) => {
  const [hov, setHov] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const disabled = type.isDeleted || saving;
  const status = rowStatus(type, acct);
  const lands = type.chartOfAccountId ? landsIn(acct) : null;

  return (
    <tr
      className={`cp-row${selected ? ' it-row-selected' : ''}${type.isDeleted ? ' it-row-deleted' : ''}`}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <td style={{ width: 40, textAlign: 'center' }}>
        <InlineCheckbox checked={selected} onChange={() => onSelect(type.id)} title="Select row" />
      </td>

      {/* Name (read-only — set by ETL) */}
      <td>
        <div className="cp-cell">
          <div className="cp-name">{type.name || '—'}</div>
          {type.locationId == null ? <div className="cp-code">org-level</div> : null}
        </div>
      </td>

      {/* Category */}
      <td style={{ minWidth: 170 }}>
        <InlineSelect
          value={type.category || null}
          options={CATEGORY_OPTIONS}
          onChange={onCategoryChange}
          placeholder="Category…"
          rowActive={hov}
          disabled={disabled}
          minWidth={180}
          ariaLabel="Category"
        />
      </td>

      {/* Chart of Account + lands-in */}
      <td style={{ minWidth: 300 }}>
        <InlineSelect
          value={type.chartOfAccountId || null}
          options={coaOptions}
          onChange={onCoaChange}
          placeholder="Select account…"
          rowActive={hov}
          disabled={disabled}
          minWidth={300}
          ariaLabel="Chart of account"
        />
        {lands ? <div className="cp-lands">lands in <strong>{lands}</strong></div> : null}
      </td>

      {/* Status */}
      <td style={{ width: 64 }}>
        <StatusDot state={status.state} title={status.title} />
      </td>

      {/* Actions */}
      <td style={{ width: 56, padding: '0 8px' }}>
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
          <div className="cp-row-actions">
            {type.isDeleted ? (
              <ToolbarButton small iconOnly variant="ghost" icon={RotateCcw} title="Restore" aria-label="Restore" onClick={() => onRestore(type.id)} />
            ) : (
              <ToolbarButton small iconOnly variant="ghost" icon={Trash2} title="Delete" aria-label="Delete" onClick={() => setConfirm(true)} />
            )}
          </div>
          {!type.isDeleted && (
            <ConfirmPopover
              open={confirm}
              onClose={() => setConfirm(false)}
              busy={busy}
              title="Delete journal type?"
              body={<><strong>{type.name}</strong> is soft-deleted; existing journal data is unaffected and you can restore it later.</>}
              confirmLabel="Delete"
              onConfirm={async () => {
                setBusy(true);
                try { await onDelete(type.id); } finally { setBusy(false); setConfirm(false); }
              }}
            />
          )}
        </div>
      </td>
    </tr>
  );
};

/** Inline "add journal type" row (top of the table). */
const AddJournalTypeRow = ({ coaOptions, saving, onSave, onCancel }) => {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const canSave = draft.name.trim().length > 0 && !saving;
  const submit = () => { if (canSave) onSave(draft); };
  return (
    <tr className="cp-row cp-row-new">
      <td />
      <td>
        <div className="cp-cell" style={{ padding: '4px 8px' }}>
          <input
            className="cp-input"
            style={{ height: 32, width: '100%' }}
            autoFocus
            value={draft.name}
            placeholder="Journal type name"
            aria-label="Journal type name"
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
          />
        </div>
      </td>
      <td>
        <InlineSelect value={draft.category} options={CATEGORY_OPTIONS} onChange={(v) => setDraft((d) => ({ ...d, category: v ?? null }))} placeholder="Category…" rowActive minWidth={180} ariaLabel="Category" />
      </td>
      <td>
        <InlineSelect value={draft.chartOfAccountId} options={coaOptions} onChange={(v) => setDraft((d) => ({ ...d, chartOfAccountId: v ?? null }))} placeholder="Select account…" rowActive minWidth={300} ariaLabel="Chart of account" />
      </td>
      <td />
      <td style={{ padding: '0 8px' }}>
        <div style={{ display: 'inline-flex', gap: 2, justifyContent: 'flex-end', width: '100%' }}>
          <ToolbarButton small iconOnly variant="primary" icon={Check} title="Add" aria-label="Add journal type" disabled={!canSave} onClick={submit} />
          <ToolbarButton small iconOnly variant="ghost" icon={X} title="Cancel" aria-label="Cancel" disabled={saving} onClick={onCancel} />
        </div>
      </td>
    </tr>
  );
};

const JournalTypesPage = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const journalMapEnabled = useFeatureFlagEnabled('journal-map');
  const { currentOrganization } = useUserSettingsContext();

  const [journalTypes, setJournalTypes] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);       // first load only
  const [refreshing, setRefreshing] = useState(false); // subsequent refetches — table stays put

  const [searchTerm, setSearchTerm] = useState('');
  const [mappingFilter, setMappingFilter] = useState('all'); // 'all' | 'unmapped' | 'mapped'
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

  const [savingId, setSavingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkMode, setBulkMode] = useState('chartOfAccountId'); // 'chartOfAccountId' | 'category'
  const [bulkApplying, setBulkApplying] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [showAiCoaMappingModal, setShowAiCoaMappingModal] = useState(false);

  const orgId = currentOrganization?.id;

  // Redirect if feature flag is disabled
  useEffect(() => {
    if (journalMapEnabled === false) navigate('/dashboard');
  }, [journalMapEnabled, navigate]);

  // Load locations
  useEffect(() => {
    if (!orgId) return;
    const params = new URLSearchParams(search);
    const locationIdFromUrl = params.get('locationId') ? parseInt(params.get('locationId'), 10) : null;
    setLocationsLoaded(false);
    getLocationsByOrgId(orgId)
      .then((data) => {
        const list = data || [];
        setLocations(list);
        const match = locationIdFromUrl && list.find((l) => l.id === locationIdFromUrl);
        setSelectedLocation(match || list[0] || null);
      })
      .catch((err) => {
        console.error('Error fetching locations:', err);
        toast.error('Could not load locations — reload the page to retry');
        setLocations([]);
        setSelectedLocation(null);
      })
      .finally(() => setLocationsLoaded(true));
  }, [orgId, search]);

  // Load chart of accounts for the selected location
  useEffect(() => {
    if (!orgId || !selectedLocation?.id) return;
    getChartOfAccounts(orgId, selectedLocation.id)
      .then((data) => setChartAccounts(data || []))
      .catch((err) => {
        console.error('Error fetching chart of accounts:', err);
        toast.error('Could not load the chart of accounts — account selects will be empty');
      });
  }, [orgId, selectedLocation]);

  // Load journal entry types
  const fetchTypes = useCallback(async ({ silent = false } = {}) => {
    if (!orgId || !selectedLocation?.id) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const data = await getJournalEntryTypes(orgId, selectedLocation.id, showDeleted);
      setJournalTypes(data || []);
    } catch (err) {
      console.error('Error fetching journal entry types:', err);
      toast.error('Could not load journal types — reload the page to retry');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orgId, selectedLocation, showDeleted]);

  useEffect(() => {
    // First paint of a location shows the LoadingScreen; later toggles refetch in place.
    fetchTypes({ silent: journalTypes.length > 0 });
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTypes]);

  // Zero-location org: nothing will ever load — release the loading screen.
  useEffect(() => {
    if (locationsLoaded && locations.length === 0) setLoading(false);
  }, [locationsLoaded, locations.length]);

  // ── Derived ───────────────────────────────────────────────────────────
  const acctById = useMemo(() => {
    const m = new Map();
    chartAccounts.forEach((a) => m.set(a.id, a));
    return m;
  }, [chartAccounts]);

  const coaOptions = useMemo(() => {
    const opts = chartAccounts.map((a) => ({
      value: a.id,
      label: `${a.accountNumber} - ${a.accountName}`,
      hint: a.department || undefined,
    }));
    // Rows can point at accounts no longer in the live list (deleted / other location).
    // Surface them as a labelled option so the select isn't silently blank.
    const seen = new Set(opts.map((o) => o.value));
    journalTypes.forEach((t) => {
      if (t.chartOfAccountId && !seen.has(t.chartOfAccountId)) {
        seen.add(t.chartOfAccountId);
        opts.push({ value: t.chartOfAccountId, label: `Account #${t.chartOfAccountId} (not in this location's live COA)` });
      }
    });
    return opts;
  }, [chartAccounts, journalTypes]);

  const locationOptions = useMemo(() => locations.map((l) => ({ value: l.id, label: l.name })), [locations]);

  const live = useMemo(() => journalTypes.filter((t) => !t.isDeleted), [journalTypes]);
  const deletedCount = journalTypes.length - live.length;
  const mappedCount = live.filter((t) => !!t.chartOfAccountId).length;
  const unmappedCount = live.length - mappedCount;
  const distinctAccounts = useMemo(() => new Set(live.filter((t) => t.chartOfAccountId).map((t) => t.chartOfAccountId)).size, [live]);

  const sorted = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return journalTypes
      .filter((t) => {
        if (q) {
          const acct = acctById.get(t.chartOfAccountId);
          const hay = [t.name, t.category, acct?.accountNumber, acct?.accountName].filter(Boolean).join(' ').toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (mappingFilter === 'unmapped') return !t.isDeleted && !t.chartOfAccountId;
        if (mappingFilter === 'mapped') return !t.isDeleted && !!t.chartOfAccountId;
        return true;
      })
      .sort((a, b) => {
        let av; let bv;
        if (sortConfig.key === 'account') {
          av = acctById.get(a.chartOfAccountId)?.accountNumber || '';
          bv = acctById.get(b.chartOfAccountId)?.accountNumber || '';
        } else {
          av = a[sortConfig.key] || '';
          bv = b[sortConfig.key] || '';
        }
        const cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase(), undefined, { numeric: true });
        return sortConfig.direction === 'ascending' ? cmp : -cmp;
      });
  }, [journalTypes, searchTerm, mappingFilter, sortConfig, acctById]);

  const visibleSelectable = useMemo(() => sorted.filter((t) => !t.isDeleted), [sorted]);
  const allVisibleSelected = visibleSelectable.length > 0 && visibleSelectable.every((t) => selected.has(t.id));
  const someVisibleSelected = visibleSelectable.some((t) => selected.has(t.id));

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending' }));
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelected((prev) => {
    if (allVisibleSelected) {
      const next = new Set(prev);
      visibleSelectable.forEach((t) => next.delete(t.id));
      return next;
    }
    const next = new Set(prev);
    visibleSelectable.forEach((t) => next.add(t.id));
    return next;
  });

  const handleUpdate = async (id, field, value) => {
    setSavingId(id);
    try {
      const updated = await updateJournalEntryType(id, orgId, { [field]: value });
      setJournalTypes((prev) => prev.map((t) => (t.id === id ? { ...t, ...updated, [field]: value } : t)));
      toast.success('Saved');
    } catch (err) {
      console.error('Error updating journal entry type:', err);
      toast.error(err?.response?.data?.error || 'Save failed — try again');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteJournalEntryType(id);
      setJournalTypes((prev) => (showDeleted
        ? prev.map((t) => (t.id === id ? { ...t, isDeleted: true } : t))
        : prev.filter((t) => t.id !== id)));
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      toast.success('Deleted');
    } catch (err) {
      console.error('Error deleting journal entry type:', err);
      toast.error('Delete failed — try again');
    }
  };

  const handleRestore = async (id) => {
    try {
      const updated = await restoreJournalEntryType(id, orgId);
      setJournalTypes((prev) => prev.map((t) => (t.id === id ? { ...t, ...updated, isDeleted: false } : t)));
      toast.success('Restored');
    } catch (err) {
      console.error('Error restoring journal entry type:', err);
      toast.error('Restore failed — try again');
    }
  };

  /** Bulk-set one field on a set of ids; returns the number of rows the API reports updated. */
  const applyBulk = async (ids, field, value) => {
    const res = await bulkUpdateJournalEntryTypes(orgId, ids, field, value);
    const idSet = new Set(ids);
    setJournalTypes((prev) => prev.map((t) => (idSet.has(t.id) ? { ...t, [field]: value } : t)));
    return typeof res?.updated === 'number' ? res.updated : ids.length;
  };

  const handleBulkApply = async (rawValue) => {
    const ids = [...selected];
    if (!ids.length) return;
    const value = bulkMode === 'chartOfAccountId'
      ? (rawValue == null ? null : parseInt(rawValue, 10))
      : (rawValue || null);
    setBulkApplying(true);
    try {
      const n = await applyBulk(ids, bulkMode, value);
      const what = bulkMode === 'chartOfAccountId' ? 'account' : 'category';
      toast.success(`Set ${what} on ${n} journal type${n === 1 ? '' : 's'}`);
      setSelected(new Set());
    } catch (err) {
      console.error('Bulk update failed:', err);
      toast.error(err?.response?.data?.error || 'Bulk update failed — nothing was changed');
    } finally {
      setBulkApplying(false);
    }
  };

  const handleApplyAiCoaMappings = async (mappings) => {
    setShowAiCoaMappingModal(false);
    // Group by target account → one bulk call per distinct account.
    const groups = new Map();
    mappings.forEach((m) => {
      if (!m.chartOfAccountId) return;
      const key = m.chartOfAccountId;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(m.journalEntryTypeId);
    });
    if (groups.size === 0) { toast.error('Nothing to apply — no suggestion had an account'); return; }
    let ok = 0; let failedGroups = 0; let attempted = 0;
    for (const [coaId, ids] of groups) {
      attempted += ids.length;
      try {
        ok += await applyBulk(ids, 'chartOfAccountId', coaId);
      } catch (err) {
        failedGroups += 1;
        console.error(`Error applying AI mapping for account ${coaId}:`, err);
      }
    }
    if (ok === 0) {
      toast.error('AI mappings could not be applied — nothing was saved. Try again or map rows by hand.');
    } else if (failedGroups > 0) {
      toast.success(`Applied ${ok} of ${attempted} mappings — ${failedGroups} account group${failedGroups === 1 ? '' : 's'} failed`);
    } else {
      toast.success(`Applied ${ok} mapping${ok === 1 ? '' : 's'}`);
    }
  };

  const handleCreate = async (draft) => {
    setAddSaving(true);
    try {
      const created = await createJournalEntryType({
        orgId, locationId: selectedLocation.id, name: draft.name.trim(),
        category: draft.category, chartOfAccountId: draft.chartOfAccountId,
      });
      setJournalTypes((prev) => [created, ...prev]);
      setAdding(false);
      toast.success(`Added ${created?.name || draft.name.trim()}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error(err?.response?.data?.error || `"${draft.name.trim()}" already exists at this location`);
      } else {
        toast.error(err?.response?.data?.error || 'Add failed — try again');
      }
    } finally {
      setAddSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  if (loading && !(locationsLoaded && locations.length === 0)) return <LoadingScreen />;

  const scope = locations.length > 0 ? (
    <ScopeSelect>
      <InlineSelect
        value={selectedLocation?.id || null}
        options={locationOptions}
        onChange={(value) => setSelectedLocation(value != null ? locations.find((l) => l.id === value) : null)}
        placeholder="Select location…"
        rowActive
        clearable={false}
        minWidth={220}
        ariaLabel="Location"
      />
    </ScopeSelect>
  ) : null;

  const stations = [
    { label: 'Journal types', value: live.length.toLocaleString(), sub: deletedCount ? `${deletedCount} deleted` : undefined },
    { label: 'COA account', value: `${mappedCount.toLocaleString()} mapped`, tone: mappedCount > 0 ? 'ok' : 'neutral', sub: distinctAccounts ? `${distinctAccounts} distinct account${distinctAccounts === 1 ? '' : 's'}` : undefined },
    ...(unmappedCount > 0 ? [{ label: 'Needs mapping', value: unmappedCount.toLocaleString(), tone: 'warn' }] : []),
    { label: 'Lands in', value: 'Journal export', mono: false, sub: unmappedCount > 0 ? 'unmapped types → residual line' : 'one line per account' },
  ];

  const emptyState = (() => {
    if (locationsLoaded && locations.length === 0) {
      return { title: 'No locations in this organization', body: 'Journal types are per location. Add a location first, then upload a journal report to auto-create its types.' };
    }
    if (journalTypes.length === 0) {
      return { title: 'No journal types yet', body: 'Upload a journal report for this location to auto-create its types, or add one by hand.' };
    }
    const filterName = mappingFilter === 'unmapped' ? 'unmapped' : mappingFilter === 'mapped' ? 'mapped' : null;
    const bits = [];
    if (searchTerm.trim()) bits.push(`match “${searchTerm.trim()}”`);
    if (filterName) bits.push(`are ${filterName}`);
    return {
      title: `No journal types ${bits.join(' and ') || 'to show'}`,
      body: mappingFilter === 'unmapped' && !searchTerm.trim()
        ? 'Every live journal type at this location has an account.'
        : 'Clear the search or switch the status filter to see more rows.',
      action: (
        <ToolbarButton variant="ghost" onClick={() => { setSearchTerm(''); setMappingFilter('all'); }}>Clear filters</ToolbarButton>
      ),
    };
  })();

  const bulkOptions = bulkMode === 'chartOfAccountId'
    ? chartAccounts.map((a) => ({ value: String(a.id), label: `${a.accountNumber} - ${a.accountName}` }))
    : CATEGORY_OPTIONS;

  return (
    <ConfigPageShell
      title="Journal map"
      subtitle="Which GL account each journal entry type posts to in the journal export."
      scope={scope}
      flow={{
        stations,
        coverage: live.length ? { mapped: mappedCount, total: live.length, label: 'mapped' } : undefined,
        onUnmappedClick: () => setMappingFilter('unmapped'),
      }}
    >
      <div className="it-frame" aria-busy={refreshing}>
        <ConfigToolbar
          search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search name, category or account…' }}
          status={{ value: mappingFilter, counts: { all: journalTypes.length, unmapped: unmappedCount, mapped: mappedCount }, onChange: setMappingFilter }}
          showDeleted={{ value: showDeleted, onChange: setShowDeleted, count: showDeleted ? deletedCount : undefined }}
          meta={`${sorted.length.toLocaleString()} journal type${sorted.length === 1 ? '' : 's'}`}
          actions={(
            <>
              <ToolbarButton variant="ai" icon={Zap} onClick={() => setShowAiCoaMappingModal(true)} disabled={!selectedLocation}>AI map COA</ToolbarButton>
              <ToolbarButton variant="primary" icon={Plus} onClick={() => setAdding(true)} disabled={!selectedLocation || adding}>Add journal type</ToolbarButton>
            </>
          )}
        />

        {selected.size > 0 && (
          <BulkActionBar
            key={bulkMode}
            count={selected.size}
            onClear={() => setSelected(new Set())}
            actionLabel={bulkMode === 'chartOfAccountId' ? 'Set account' : 'Set category'}
            options={bulkOptions}
            placeholder={bulkMode === 'chartOfAccountId' ? 'Choose account…' : 'Choose category…'}
            applyLabel="Apply"
            applying={bulkApplying}
            onApply={handleBulkApply}
          >
            <div style={{ display: 'inline-flex', gap: 4 }} role="group" aria-label="Bulk field">
              {[{ key: 'chartOfAccountId', label: 'Account' }, { key: 'category', label: 'Category' }].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setBulkMode(m.key)}
                  aria-pressed={bulkMode === m.key}
                  style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'var(--font-sans)', border: '1px solid rgba(255,255,255,.14)',
                    background: bulkMode === m.key ? 'rgba(255,255,255,.14)' : 'transparent',
                    color: bulkMode === m.key ? 'var(--lodge-onyx-50)' : 'rgba(255,255,255,.55)',
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </BulkActionBar>
        )}

        {sorted.length === 0 && !adding ? (
          <ConfigEmptyState {...emptyState} />
        ) : (
          <div className="it-scroll">
            <table className="it-tbl" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  <th style={{ width: 40, textAlign: 'center', padding: 0 }}>
                    <InlineCheckbox
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={visibleSelectable.length === 0}
                      title="Select all visible"
                    />
                  </th>
                  <th className="it-sort" onClick={() => handleSort('name')}>
                    Journal type <SortIcon columnKey="name" sortKey={sortConfig.key} direction={sortConfig.direction} />
                  </th>
                  <th className="it-sort" style={{ minWidth: 170 }} onClick={() => handleSort('category')}>
                    Category <SortIcon columnKey="category" sortKey={sortConfig.key} direction={sortConfig.direction} />
                  </th>
                  <th className="it-sort" style={{ minWidth: 300 }} onClick={() => handleSort('account')}>
                    COA account <SortIcon columnKey="account" sortKey={sortConfig.key} direction={sortConfig.direction} />
                  </th>
                  <th style={{ width: 64, textAlign: 'center' }}>Status</th>
                  <th style={{ width: 56 }} />
                </tr>
              </thead>
              <tbody>
                {adding && (
                  <AddJournalTypeRow coaOptions={coaOptions} saving={addSaving} onSave={handleCreate} onCancel={() => setAdding(false)} />
                )}
                {sorted.map((type) => (
                  <JournalTypeRow
                    key={type.id}
                    type={type}
                    acct={acctById.get(type.chartOfAccountId)}
                    coaOptions={coaOptions}
                    saving={savingId === type.id}
                    selected={selected.has(type.id)}
                    onSelect={toggleSelect}
                    onCategoryChange={(value) => handleUpdate(type.id, 'category', value ?? null)}
                    onCoaChange={(value) => handleUpdate(type.id, 'chartOfAccountId', value != null ? value : null)}
                    onDelete={handleDelete}
                    onRestore={handleRestore}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AiJournalCoaMappingModal
        isOpen={showAiCoaMappingModal}
        onClose={() => setShowAiCoaMappingModal(false)}
        journalEntryTypes={journalTypes}
        chartAccounts={chartAccounts}
        orgId={orgId}
        locationId={selectedLocation?.id}
        onApplyMappings={handleApplyAiCoaMappings}
      />
    </ConfigPageShell>
  );
};

export default JournalTypesPage;
